<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue"
import { useBookContentsFilter } from "../composables/useBookContentsFilter"
import { dispatchOpenContentWindow } from "../composables/useWindowRegistry"
import { shouldOpenInContentWindow } from "../utils/content_window_link"
import TypeFilterSelect from "./TypeFilterSelect.vue"

const props = defineProps({
  itemsByLetter: { type: Object, required: true },
  typeOptions: { type: Array, required: true },
  labels: { type: Object, default: () => ({}) },
  openInWindow: { type: Boolean, default: true },
})

const label = (key, fallback) => props.labels[key] || fallback
const searchInput = ref(null)

const {
  query,
  typeFilter,
  viewMode,
  currentLetter,
  showScrollToFilters,
  letters,
  sortedGroups,
  showNoResults,
  letterHasItems,
  sectionId,
  jumpToLetter,
  setViewMode,
  setTypeFilter,
  handleScroll,
  scrollToFilters,
} = useBookContentsFilter(props.itemsByLetter)

function onScroll() {
  handleScroll()
}

function scrollToFiltersAndFocus() {
  scrollToFilters(".book-contents-sidebar")
  setTimeout(() => searchInput.value?.focus(), 500)
}

function openItem(item, event) {
  if (!props.openInWindow || !shouldOpenInContentWindow(event)) return
  event.preventDefault()
  event.stopPropagation()
  dispatchOpenContentWindow({
    id: item.id,
    title: item.name,
    url: item.url,
  })
}

function updateStatTagActiveState(type) {
  document.querySelectorAll(".book-stats-tags .stat-tag").forEach((tag) => {
    const tagType = tag.dataset.type || "all"
    tag.classList.toggle("active", tagType === type)
  })
}

function onFilterTypeEvent(event) {
  setTypeFilter(event.detail.type || "all")
  scrollToFilters(".book-contents-sidebar")
}

watch(typeFilter, (value) => {
  updateStatTagActiveState(value)
})

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true })
  document.addEventListener("book-contents:filter-type", onFilterTypeEvent)
  updateStatTagActiveState(typeFilter.value)
})

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll)
  document.removeEventListener("book-contents:filter-type", onFilterTypeEvent)
})
</script>

<template>
  <div class="book-contents-browser list-page-body">
    <aside
      class="list-page-sidebar book-contents-sidebar"
      :aria-label="label('sidebarNavigation', 'Navigation')"
    >
      <div class="list-page-sidebar-inner">
        <div class="list-sidebar-section">
          <h2 class="list-sidebar-title">
            {{ label("searchTitle", "Search") }}
          </h2>
          <div class="book-toolbar">
            <div class="book-toolbar-inner">
              <div class="search-box">
                <i class="bi bi-search" />
                <input
                  ref="searchInput"
                  v-model="query"
                  type="text"
                  class="form-control"
                  :placeholder="label('searchPlaceholder', 'Search in this book...')"
                >
              </div>
            </div>
          </div>
        </div>

        <div v-if="typeOptions.length > 1" class="list-sidebar-section">
          <h2 class="list-sidebar-title">
            {{ label("categoryTitle", "Category") }}
          </h2>
          <div class="book-filters-row">
            <TypeFilterSelect
              :options="typeOptions"
              :model-value="typeFilter"
              :search-placeholder="label('searchTypes', 'Search types...')"
              @update:model-value="setTypeFilter"
            />
          </div>
        </div>

        <div class="list-sidebar-section">
          <h2 class="list-sidebar-title">
            {{ label("viewTitle", "View") }}
          </h2>
          <div class="view-toggle">
            <button
              type="button"
              class="view-btn"
              :class="{ active: viewMode === 'list' }"
              data-bs-toggle="tooltip"
              :data-bs-title="label('viewList', 'List view')"
              @click="setViewMode('list')"
            >
              <i class="bi bi-list" />
            </button>
            <button
              type="button"
              class="view-btn"
              :class="{ active: viewMode === 'cards' }"
              data-bs-toggle="tooltip"
              :data-bs-title="label('viewCards', 'Card view')"
              @click="setViewMode('cards')"
            >
              <i class="bi bi-grid-3x3-gap" />
            </button>
          </div>
        </div>

        <div class="list-sidebar-section">
          <h2 class="list-sidebar-title">
            {{ label("alphabetTitle", "A–Z") }}
          </h2>
          <nav class="alphabet-nav alphabet-nav--vertical">
            <template v-for="letter in letters" :key="letter">
              <a
                v-if="letterHasItems(letter)"
                :href="`#${sectionId(letter)}`"
                class="alphabet-link"
                :class="{ current: currentLetter === letter }"
                @click="jumpToLetter($event, letter)"
              >{{ letter }}</a>
              <span v-else class="alphabet-link disabled">{{ letter }}</span>
            </template>
          </nav>
        </div>
      </div>
    </aside>

    <div class="list-page-main">
      <div class="book-contents-wrapper">
        <div
          class="contents-list view-list"
          :class="{ active: viewMode === 'list' }"
        >
          <section
            v-for="[letter, items] in sortedGroups"
            :id="`letter-${letter}`"
            :key="`list-${letter}`"
            class="letter-group"
            :data-letter="letter"
          >
            <h3 class="letter-heading">
              {{ letter }}
            </h3>

            <div class="items-container">
              <div
                v-for="item in items"
                :key="`list-item-${item.id}`"
                class="content-item"
              >
                <a
                  :href="item.url"
                  class="item-link"
                  data-turbo="false"
                  :data-content-window-id="openInWindow ? item.id : null"
                  :data-content-window-title="openInWindow ? item.name : null"
                  @click="openItem(item, $event)"
                >
                  <div class="item-icon">
                    <img
                      v-if="item.imageUrl"
                      :src="item.imageUrl"
                      :alt="item.name"
                      class="type-icon"
                      loading="lazy"
                    >
                    <img
                      v-else-if="item.typeIconUrl"
                      :src="item.typeIconUrl"
                      :alt="item.typeLabel"
                      class="type-icon"
                    >
                    <i v-else-if="item.typeIconClass" :class="item.typeIconClass" />
                  </div>

                  <div class="item-content">
                    <h4 class="item-name">
                      {{ item.name }}
                    </h4>
                    <p v-if="item.description" class="item-description">
                      {{ item.description }}
                    </p>
                    <span class="item-type-badge">{{ item.typeLabel }}</span>
                  </div>

                  <i v-if="item.favorited" class="bi bi-star-fill favorite-indicator" />
                </a>
              </div>
            </div>
          </section>
        </div>

        <div
          class="contents-list view-cards"
          :class="{ active: viewMode === 'cards' }"
        >
          <section
            v-for="[letter, items] in sortedGroups"
            :id="`letter-${letter}-cards`"
            :key="`cards-${letter}`"
            class="letter-group"
            :data-letter="letter"
          >
            <h3 class="letter-heading">
              {{ letter }}
            </h3>

            <div class="cards-grid">
              <a
                v-for="item in items"
                :key="`card-item-${item.id}`"
                :href="item.url"
                class="content-card card-link"
                data-turbo="false"
                :data-content-window-id="openInWindow ? item.id : null"
                :data-content-window-title="openInWindow ? item.name : null"
                @click="openItem(item, $event)"
              >
                <div v-if="item.imageUrl" class="card-image">
                  <img :src="item.imageUrl" :alt="item.name" loading="lazy">
                </div>
                <div
                  v-else
                  class="card-icon"
                  :style="{ background: item.gradient }"
                >
                  <img
                    v-if="item.typeIconUrl"
                    :src="item.typeIconUrl"
                    :alt="item.typeLabel"
                    class="type-icon"
                    width="32"
                    height="32"
                  >
                  <i v-else-if="item.typeIconClass" :class="item.typeIconClass" />
                </div>

                <div class="card-body">
                  <h4 class="card-title">
                    {{ item.name }}
                  </h4>
                  <p v-if="item.description" class="card-description">
                    {{ item.description }}
                  </p>
                  <span class="card-type-badge">{{ item.typeLabel }}</span>
                </div>

                <i v-if="item.favorited" class="bi bi-star-fill favorite-indicator" />
              </a>
            </div>
          </section>
        </div>

        <div v-if="showNoResults" class="no-results">
          <i class="bi bi-search" />
          <p>{{ label("noResults", "No results found") }}</p>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="btn-scroll-to-filters"
      :class="{ visible: showScrollToFilters }"
      data-bs-toggle="tooltip"
      :data-bs-title="label('scrollToFilters', 'Jump to filters')"
      @click="scrollToFiltersAndFocus"
    >
      <i class="bi bi-funnel" />
    </button>
  </div>
</template>
